/**
 * Authentication service
 * Handles login, registration, password reset, and user invitations
 */

import { supabase } from "./supabase";
import type { UserRole } from "../types/auth.types";
import type {
  LoginCredentials,
  AuthResponse,
  Profile,
  UserInvitation,
  PasswordResetRequest,
  PasswordResetConfirmation,
} from "../types/auth.types";
import type { UUID } from "../types/common.types";

/**
 * Sign in with email and password
 */
export const login = async (
  credentials: LoginCredentials,
): Promise<AuthResponse> => {
  console.log("[Auth] login: Starting");

  // Clear any stale local session before creating a new one
  await supabase.auth.signOut({ scope: "local" }).catch(() => {});

  const { data, error } = await supabase.auth.signInWithPassword({
    email: credentials.email,
    password: credentials.password,
  });

  console.log("[Auth] login: signInWithPassword returned", {
    hasData: !!data,
    hasError: !!error,
  });

  if (error) {
    throw error;
  }
  if (!data.user) {
    throw new Error("No user returned from login");
  }

  console.log("[Auth] login: Calling getProfile for user:", data.user.id);

  // Fetch user profile
  const profile = await getProfile(data.user.id);

  console.log(
    "[Auth] login: getProfile returned, role:",
    profile.role,
    "is_active:",
    profile.is_active,
  );

  // Check if account is active
  if (!profile.is_active) {
    console.log("[Auth] login: Account is deactivated");
    await supabase.auth.signOut(); // Sign them out immediately
    throw new Error(
      "Your account has been deactivated. Please contact support for assistance.",
    );
  }

  return {
    user: profile,
    session: {
      access_token: data.session?.access_token || "",
      refresh_token: data.session?.refresh_token || "",
      expires_in: data.session?.expires_in || 0,
      expires_at: data.session?.expires_at?.toString() || "",
    },
  };
};

/**
 * Get user profile by ID
 */
export const getProfile = async (userId: UUID): Promise<Profile> => {
  console.log("[Auth] getProfile: Starting for userId:", userId);

  const { data, error } = await supabase
    .from("profiles")
    .select("*")
    .eq("id", userId)
    .single();

  console.log("[Auth] getProfile: Query returned", {
    hasData: !!data,
    hasError: !!error,
  });

  if (error) {
    throw error;
  }
  if (!data) {
    throw new Error("Profile not found");
  }

  return data as Profile;
};

/**
 * Update user profile
 */
export const updateProfile = async (
  userId: UUID,
  updates: Partial<Profile>,
): Promise<Profile> => {
  const { data, error } = await supabase
    .from("profiles")
    .update(updates)
    .eq("id", userId)
    .select()
    .single();

  if (error) {
    throw error;
  }
  return data as Profile;
};

/*
 * Two-factor authentication: REMOVED 2026-10-03 (dead, half-built feature).
 * enable2FA/disable2FA wrote profiles.two_factor_enabled / two_factor_phone, and login read
 * two_factor_enabled. Neither column exists on QA or PROD; the send-2fa-code / verify-2fa-code
 * edge functions were never written; there was no /2fa route and no UI that enabled it;
 * auth.mfa_factors is empty in both environments. Nobody could reach it, and anything that did
 * would have failed. Real 2FA, if wanted, should use Supabase Auth MFA (TOTP), not profile columns.
 */

/**
 * Request password reset
 */
export const requestPasswordReset = async (
  request: PasswordResetRequest,
): Promise<void> => {
  const { error } = await supabase.auth.resetPasswordForEmail(request.email, {
    redirectTo: `${window.location.origin}/reset-password`,
  });

  if (error) {
    throw error;
  }
};

/**
 * Confirm password reset with new password
 */
export const confirmPasswordReset = async (
  confirmation: PasswordResetConfirmation,
): Promise<void> => {
  const { error } = await supabase.auth.updateUser({
    password: confirmation.password,
  });

  if (error) {
    throw error;
  }
};

/**
 * Create user invitation
 */
export const createInvitation = async (
  email: string,
  role: UserRole,
  organizationId?: UUID,
  locationId?: UUID,
): Promise<UserInvitation> => {
  const { data: userData } = await supabase.auth.getUser();
  if (!userData.user) {
    throw new Error("Not authenticated");
  }

  const { data, error } = await supabase
    .from("user_invitations")
    .insert({
      email,
      role,
      organization_id: organizationId,
      location_id: locationId,
      invited_by: userData.user.id,
      status: "pending",
      expires_at: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(), // 7 days
    })
    .select()
    .single();

  if (error) {
    throw error;
  }
  return data as UserInvitation;
};

/**
 * Get invitation by token
 */
export const getInvitation = async (token: string): Promise<UserInvitation> => {
  const { data, error } = await supabase
    .from("user_invitations")
    .select("*")
    .eq("token", token)
    .eq("status", "pending")
    .single();

  if (error) {
    throw error;
  }
  if (!data) {
    throw new Error("Invitation not found or expired");
  }

  // Check if invitation is expired
  if (new Date(data.expires_at) < new Date()) {
    throw new Error("Invitation has expired");
  }

  return data as UserInvitation;
};

/**
 * Accept invitation and create user account
 */
export const acceptInvitation = async (
  token: string,
  password: string,
  firstName: string,
  lastName: string,
): Promise<Profile> => {
  // Get invitation details
  const invitation = await getInvitation(token);

  // Create user account
  const { data: authData, error: authError } = await supabase.auth.signUp({
    email: invitation.email,
    password,
  });

  if (authError) {
    throw authError;
  }
  if (!authData.user) {
    throw new Error("Failed to create user account");
  }

  // Create profile
  const { data: profile, error: profileError } = await supabase
    .from("profiles")
    .insert({
      id: authData.user.id,
      email: invitation.email,
      full_name: `${firstName} ${lastName}`,
      role: invitation.role,
    })
    .select()
    .single();

  if (profileError) {
    throw profileError;
  }

  // Mark invitation as accepted
  await supabase
    .from("user_invitations")
    .update({
      status: "accepted",
      accepted_at: new Date().toISOString(),
    })
    .eq("id", invitation.id);

  return profile as Profile;
};

/**
 * Cancel a pending invitation via the cancel-pending-invitation edge
 * function. The function handles the full multi-step cleanup
 * (user_invitations + invitation_codes + auth.users + profile +
 * auto-created support_agents) and returns a structured summary.
 *
 * Why an edge function (not a direct table UPDATE): the original
 * implementation did `UPDATE user_invitations SET status='cancelled'`
 * but user_invitations has NO `status` column. The UPDATE was a
 * silent no-op since the function was written. Auth.users deletion
 * also requires the service-role admin API, which the frontend
 * cannot call directly. The edge function gates by operator JWT
 * (superadmin/admin/support_admin) then switches to a service-role
 * client internally.
 *
 * See docs/AUDIT_PROMPT_SOFT_DELETE_AGENTS_AND_CANCEL_INVITATIONS.md
 * for the full structured-summary shape.
 */
export interface CancelInvitationSummary {
  ok: boolean;
  invitation_id: string | null;
  email_used: string | null;
  user_invitations_deleted: number;
  invitation_codes_deleted_count: number;
  auth_user_deleted: boolean;
  auth_delete_skipped_reason:
    | "no_invitation_code_user_id"
    | "email_mismatch"
    | "has_signed_in"
    | "auth_user_not_found"
    | null;
  profile_deleted: boolean;
  support_agent_deleted: boolean;
  support_agent_left_alone_reason:
    | "support_agent_has_references"
    | "support_agent_inactive"
    | "not_present"
    | null;
  resolved_user_id: string | null;
  resolved_via: "invitation_codes_user_id" | "email_fallback" | "no_match";
}

export const cancelInvitation = async (
  invitationId: UUID,
): Promise<CancelInvitationSummary> => {
  const session = (await supabase.auth.getSession()).data.session;
  if (!session) {
    throw new Error("Not authenticated");
  }
  const url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/cancel-pending-invitation`;
  const res = await fetch(url, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${session.access_token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ invitation_id: invitationId }),
  });
  const json = (await res.json()) as
    | CancelInvitationSummary
    | { error?: string };
  if (!res.ok) {
    const msg =
      (json as { error?: string }).error ??
      `Cancel failed (HTTP ${res.status})`;
    throw new Error(msg);
  }
  return json as CancelInvitationSummary;
};

/**
 * List pending (not yet accepted) user invitations.
 * Surfaced on the Admin > Pending Invitations page.
 */
export interface PendingInvitationRow {
  id: UUID;
  email: string;
  full_name: string;
  title: string | null;
  role: string;
  token: string;
  expires_at: string;
  created_at: string;
  invited_by: UUID | null;
}

export const getPendingInvitations = async (): Promise<
  PendingInvitationRow[]
> => {
  const { data, error } = await supabase
    .from("user_invitations")
    .select(
      "id, email, full_name, title, role, token, expires_at, created_at, invited_by",
    )
    .is("accepted_at", null)
    .order("created_at", { ascending: false });
  if (error) {
    throw error;
  }
  return (data ?? []) as PendingInvitationRow[];
};

/**
 * Get current authenticated user profile
 * Note: Caching is handled by AuthContext, not this function
 */
export const getCurrentUserProfile = async (): Promise<Profile | null> => {
  console.log("[Auth] getCurrentUserProfile: Starting");

  try {
    // Get current user from Supabase auth
    const { data: userData, error } = await supabase.auth.getUser();

    if (error) {
      console.error("[Auth] getCurrentUserProfile: Error from getUser", error);
      // Try getSession as fallback
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (session?.user) {
        console.log(
          "[Auth] getCurrentUserProfile: Fallback to getSession succeeded",
        );
        return getProfile(session.user.id);
      }
      return null;
    }

    console.log("[Auth] getCurrentUserProfile: getUser returned", {
      hasUser: !!userData.user,
    });
    if (!userData.user) {
      return null;
    }

    return getProfile(userData.user.id);
  } catch (error) {
    console.error("[Auth] getCurrentUserProfile: Unexpected error", error);
    // Try getSession as fallback
    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (session?.user) {
        console.log(
          "[Auth] getCurrentUserProfile: Fallback to getSession succeeded",
        );
        return getProfile(session.user.id);
      }
    } catch (fallbackError) {
      console.error(
        "[Auth] getCurrentUserProfile: Fallback also failed",
        fallbackError,
      );
    }
    return null;
  }
};

/**
 * Refresh authentication session
 */
export const refreshSession = async (): Promise<void> => {
  const { error } = await supabase.auth.refreshSession();
  if (error) {
    throw error;
  }
};

/**
 * Invite user via Edge Function
 * Calls the invite-user edge function which uses SendGrid for role-specific invitation emails
 */
export const inviteUserViaEdgeFunction = async (
  email: string,
  role: UserRole,
  organizationId?: UUID,
  locationIds?: UUID[],
  fullName?: string,
  resend: boolean = false,
): Promise<{ success: boolean; user?: any; message?: string }> => {
  console.log("[inviteUserViaEdgeFunction] Starting invitation process:", {
    email,
    role,
    organizationId: organizationId || "undefined",
    locationIds: locationIds || [],
    fullName: fullName || "undefined",
    resend,
  });

  // Get current session token for authentication
  const {
    data: { session },
  } = await supabase.auth.getSession();

  if (!session) {
    console.error("[inviteUserViaEdgeFunction] No active session");
    throw new Error("No active session. Please log in again.");
  }

  console.log(
    "[inviteUserViaEdgeFunction] Session found, calling edge function",
  );

  // Call the edge function
  const { data, error } = await supabase.functions.invoke("invite-user", {
    body: {
      email,
      role,
      organization_id: organizationId,
      location_ids: locationIds || [],
      full_name: fullName,
      resend,
    },
    headers: {
      Authorization: `Bearer ${session.access_token}`,
    },
  });

  console.log("[inviteUserViaEdgeFunction] Edge function response:", {
    hasError: !!error,
    errorDetails: error,
    errorContext: error?.context,
    data,
    dataType: typeof data,
    dataKeys: data ? Object.keys(data) : null,
    dataError: data?.error,
  });

  // IMPORTANT: Check data.error FIRST - this contains the actual error message from the edge function
  // When edge function returns non-2xx status (like 409), the response body is in data.error
  if (data && data.error) {
    console.error(
      "[inviteUserViaEdgeFunction] Data contains error:",
      data.error,
    );
    throw new Error(data.error);
  }

  // Handle generic edge function errors (network issues, etc.)
  if (error) {
    console.error("[inviteUserViaEdgeFunction] Edge function error:", error);

    // Try to extract a meaningful error message
    let errorMessage = "Failed to send invitation";

    // Check if error.context is a Response object (contains the actual edge function response)
    if (error.context && error.context instanceof Response) {
      try {
        // Parse the JSON response body to get the error message
        const responseBody = await error.context.json();
        console.log(
          "[inviteUserViaEdgeFunction] Parsed error response:",
          responseBody,
        );
        if (responseBody.error) {
          errorMessage = responseBody.error;
        }
      } catch (parseError) {
        console.error(
          "[inviteUserViaEdgeFunction] Failed to parse error response:",
          parseError,
        );
      }
    } else if (error.context) {
      // The context might contain the actual response body directly
      const contextError = error.context;
      if (typeof contextError === "object" && contextError.error) {
        errorMessage = contextError.error;
      } else if (typeof contextError === "string") {
        errorMessage = contextError;
      }
    }

    // Fall back to error.message if available
    if (!errorMessage || errorMessage === "Failed to send invitation") {
      errorMessage = error.message || "Failed to send invitation";
    }

    throw new Error(errorMessage);
  }

  console.log("[inviteUserViaEdgeFunction] Invitation successful");
  return data;
};

/**
 * Get user auth status from auth.users table
 * Requires superadmin access
 */
export const getUserAuthStatus = async (
  userId: UUID,
): Promise<{
  email_confirmed_at: string | null;
  last_sign_in_at: string | null;
  created_at: string;
  confirmed: boolean;
}> => {
  console.log("[getUserAuthStatus] Getting auth status for user:", userId);

  const session = await supabase.auth.getSession();
  if (!session.data.session) {
    throw new Error("No active session");
  }

  const { data, error } = await supabase.functions.invoke("manage-user", {
    body: {
      action: "get_status",
      user_id: userId,
    },
    headers: {
      Authorization: `Bearer ${session.data.session.access_token}`,
    },
  });

  if (error) {
    console.error("[getUserAuthStatus] Error:", error);
    throw error;
  }

  console.log("[getUserAuthStatus] Status retrieved:", data);
  return data;
};

/**
 * Delete user from both auth.users and profiles
 * Requires superadmin access
 */
export const deleteUser = async (userId: UUID): Promise<void> => {
  console.log("[deleteUser] Deleting user:", userId);

  const session = await supabase.auth.getSession();
  if (!session.data.session) {
    throw new Error("No active session");
  }

  const { data, error } = await supabase.functions.invoke("manage-user", {
    body: {
      action: "delete",
      user_id: userId,
    },
    headers: {
      Authorization: `Bearer ${session.data.session.access_token}`,
    },
  });

  if (error) {
    console.error("[deleteUser] Error:", error);
    throw error;
  }

  if (data && data.error) {
    console.error("[deleteUser] Data contains error:", data.error);
    throw new Error(data.error);
  }

  console.log("[deleteUser] User deleted successfully");
};

/**
 * Send password reset email via SendGrid
 * Uses the send-auth-email edge function for branded emails
 */
export const sendPasswordResetEmail = async (
  email: string,
): Promise<{ success: boolean }> => {
  console.log("[sendPasswordResetEmail] Sending password reset to:", email);

  const { data, error } = await supabase.functions.invoke("send-auth-email", {
    body: {
      type: "password_reset",
      email,
    },
  });

  if (error) {
    console.error("[sendPasswordResetEmail] Error:", error);
    throw error;
  }

  if (data && data.error) {
    console.error("[sendPasswordResetEmail] Data contains error:", data.error);
    throw new Error(data.error);
  }

  console.log("[sendPasswordResetEmail] Email sent successfully");
  return { success: true };
};

/**
 * Send email change confirmation via SendGrid
 * Sends confirmation to the NEW email address
 */
export const sendEmailChangeConfirmation = async (
  currentEmail: string,
  newEmail: string,
): Promise<{ success: boolean }> => {
  console.log("[sendEmailChangeConfirmation] Sending email change:", {
    currentEmail,
    newEmail,
  });

  const session = await supabase.auth.getSession();
  if (!session.data.session) {
    throw new Error("No active session");
  }

  const { data, error } = await supabase.functions.invoke("send-auth-email", {
    body: {
      type: "email_change",
      email: currentEmail,
      new_email: newEmail,
    },
    headers: {
      Authorization: `Bearer ${session.data.session.access_token}`,
    },
  });

  if (error) {
    console.error("[sendEmailChangeConfirmation] Error:", error);
    throw error;
  }

  if (data && data.error) {
    console.error(
      "[sendEmailChangeConfirmation] Data contains error:",
      data.error,
    );
    throw new Error(data.error);
  }

  console.log("[sendEmailChangeConfirmation] Email sent successfully");
  return { success: true };
};
